/* Self-contained module (spec.md §3.0 pattern). Handles: tab switching between the
   Heavy Bag / Duffel Bag panels, thumbnail -> main image swap, variant selection,
   add-on checkbox price math, and a single multi-line-item /cart/add.js call that
   submits the chosen variant plus any checked add-on variants together. */
import { CartLinesUpdateEvent } from "@shopify/events";

(function () {
  var root = document.querySelector(".pr-product-showcase");
  if (!root) {
    return;
  }

  var moneyFormatter = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0
  });

  function formatMoney(amountInSubunits) {
    // Shopify Liquid's bare (unfiltered) price/variant.price output is always in
    // the shop's smallest currency subunit (paise, not rupees) — divide by 100.
    return moneyFormatter.format(amountInSubunits / 100);
  }

  var tabs = root.querySelectorAll("[data-pr-showcase-tab]");
  var panels = root.querySelectorAll("[data-pr-showcase-panel]");

  tabs.forEach(function (tab) {
    tab.addEventListener("click", function () {
      tabs.forEach(function (t) {
        t.classList.remove("pr-product-showcase__tab--active");
        t.setAttribute("aria-selected", "false");
      });
      tab.classList.add("pr-product-showcase__tab--active");
      tab.setAttribute("aria-selected", "true");
      prTrack("tab_" + prSlug(tab.textContent));

      var targetId = tab.getAttribute("data-target");
      panels.forEach(function (panel) {
        panel.classList.toggle("pr-product-showcase__panel--hidden", panel.id !== targetId);
      });
    });
  });

  panels.forEach(function (panel) {
    var mainImage = panel.querySelector("[data-pr-showcase-main-image]");
    var thumbs = panel.querySelectorAll("[data-pr-showcase-thumb]");
    var photoNumber = function (thumb) {
      return Array.prototype.indexOf.call(thumbs, thumb) + 1;
    };
    // photo_view_03: which photos actually get looked at, however they got there.
    var trackPhoto = function (number) {
      prTrack("photo_view_" + (number < 10 ? "0" : "") + number);
    };

    // Stretch-to-fill only suits near-4:5 frames. Anything further off (e.g. the
    // 9:16 phone shot) would visibly squash, so letterbox it instead.
    function fitMainImage() {
      if (!mainImage.naturalWidth) {
        return;
      }
      var ratio = mainImage.naturalWidth / mainImage.naturalHeight;
      var offFrame = Math.abs(ratio / 0.8 - 1) > 0.1;
      mainImage.classList.toggle("pr-product-showcase__main-img--contain", offFrame);
    }
    if (mainImage) {
      mainImage.addEventListener("load", fitMainImage);
      fitMainImage();
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener("click", function (e) {
        // Swipes and the full-screen viewer drive thumbs with .click() and track themselves.
        if (e.isTrusted) {
          prTrack("gallery_thumb_click");
          trackPhoto(photoNumber(thumb));
        }
        if (mainImage) {
          mainImage.src = thumb.getAttribute("data-full");
        }
        thumbs.forEach(function (t) {
          t.classList.remove("pr-product-showcase__thumb--active");
        });
        thumb.classList.add("pr-product-showcase__thumb--active");
        scrollThumbIntoView(thumb);
      });
    });

    // Keeps the active thumb visible inside its own scroll strip without
    // scrolling the page (element.scrollIntoView would move the page too).
    function scrollThumbIntoView(thumb) {
      var strip = thumb.parentElement;
      var vertical = strip.scrollHeight > strip.clientHeight + 1;
      if (vertical) {
        strip.scrollTo({ top: thumb.offsetTop - strip.offsetTop - (strip.clientHeight - thumb.offsetHeight) / 2, behavior: "smooth" });
      } else {
        strip.scrollTo({ left: thumb.offsetLeft - strip.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2, behavior: "smooth" });
      }
    }

    // Swipe left/right on the main photo = next/previous image (wraps around).
    // touch-action: pan-y (CSS) leaves vertical page scrolling to the browser;
    // a gesture only counts as a swipe if it's clearly more horizontal.
    var mainBox = panel.querySelector(".pr-product-showcase__main");
    if (mainBox && thumbs.length > 1) {
      var startX = null;
      var startY = null;
      mainBox.addEventListener("pointerdown", function (e) {
        startX = e.clientX;
        startY = e.clientY;
      });
      mainBox.addEventListener("pointercancel", function () {
        startX = null;
      });
      mainBox.addEventListener("pointerup", function (e) {
        if (startX === null) {
          return;
        }
        var dx = e.clientX - startX;
        var dy = e.clientY - startY;
        startX = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) {
          return;
        }
        var list = Array.prototype.slice.call(thumbs);
        var current = list.findIndex(function (t) {
          return t.classList.contains("pr-product-showcase__thumb--active");
        });
        var step = dx < 0 ? 1 : -1;
        var next = (current + step + list.length) % list.length;
        prTrack("gallery_swipe");
        trackPhoto(next + 1);
        mainBox.classList.remove("pr-product-showcase__main--from-left", "pr-product-showcase__main--from-right");
        void mainBox.offsetWidth;
        mainBox.classList.add(step > 0 ? "pr-product-showcase__main--from-right" : "pr-product-showcase__main--from-left");
        list[next].click();
      });
      mainBox.addEventListener("dragstart", function (e) {
        e.preventDefault();
      });
    }

    // Tap/click the main photo = full-screen viewer (merchant chose click over
    // hover-zoom, which doesn't exist on phones). A <dialog> opened with
    // showModal() sits in the top layer, above the fixed nav and sticky bar,
    // and gets Esc-to-close for free. Shows the whole photo (contain), with
    // a close button, prev/next arrows, swipe and arrow keys.
    if (mainBox && thumbs.length) {
      var thumbList = Array.prototype.slice.call(thumbs);
      var lightbox = document.createElement("dialog");
      lightbox.className = "pr-lightbox";
      lightbox.setAttribute("aria-label", "Product photos");
      lightbox.innerHTML =
        '<button type="button" class="pr-lightbox__close" aria-label="Close">&times;</button>' +
        '<button type="button" class="pr-lightbox__nav pr-lightbox__nav--prev" aria-label="Previous photo">&#8249;</button>' +
        '<img class="pr-lightbox__img" alt="">' +
        '<button type="button" class="pr-lightbox__nav pr-lightbox__nav--next" aria-label="Next photo">&#8250;</button>' +
        '<p class="pr-lightbox__count"></p>';
      document.body.appendChild(lightbox);
      var lbImg = lightbox.querySelector(".pr-lightbox__img");
      var lbCount = lightbox.querySelector(".pr-lightbox__count");
      var lbIndex = 0;

      var showLightbox = function (i, method) {
        lbIndex = (i + thumbList.length) % thumbList.length;
        if (method) {
          prTrack(method);
          trackPhoto(lbIndex + 1);
        }
        lbImg.src = thumbList[lbIndex].getAttribute("data-full").replace(/width=\d+/, "width=2000");
        lbCount.textContent = lbIndex + 1 + " / " + thumbList.length;
      };
      var activeIndex = function () {
        return Math.max(0, thumbList.findIndex(function (t) {
          return t.classList.contains("pr-product-showcase__thumb--active");
        }));
      };

      // A click also fires at the end of a swipe on the main photo — only a
      // near-stationary press counts as "open".
      var pressX = null;
      var pressY = null;
      mainBox.addEventListener("pointerdown", function (e) {
        pressX = e.clientX;
        pressY = e.clientY;
      });
      mainBox.style.cursor = "zoom-in";
      mainBox.addEventListener("click", function (e) {
        if (pressX !== null && (Math.abs(e.clientX - pressX) > 10 || Math.abs(e.clientY - pressY) > 10)) {
          return;
        }
        showLightbox(activeIndex());
        prTrack("fullscreen_open");
        lightbox.showModal();
        document.documentElement.classList.add("pr-lightbox-open");
      });

      lightbox.querySelector(".pr-lightbox__close").addEventListener("click", function () {
        lightbox.close();
      });
      lightbox.querySelector(".pr-lightbox__nav--prev").addEventListener("click", function () {
        showLightbox(lbIndex - 1, "fullscreen_arrow");
      });
      lightbox.querySelector(".pr-lightbox__nav--next").addEventListener("click", function () {
        showLightbox(lbIndex + 1, "fullscreen_arrow");
      });
      lightbox.addEventListener("keydown", function (e) {
        if (e.key === "ArrowRight") {
          showLightbox(lbIndex + 1, "fullscreen_key");
        } else if (e.key === "ArrowLeft") {
          showLightbox(lbIndex - 1, "fullscreen_key");
        }
      });
      // Clicking the dark backdrop (not the photo or a control) also closes.
      lightbox.addEventListener("click", function (e) {
        if (e.target === lightbox) {
          lightbox.close();
        }
      });
      var lbStartX = null;
      var lbStartY = null;
      lbImg.addEventListener("pointerdown", function (e) {
        lbStartX = e.clientX;
        lbStartY = e.clientY;
      });
      lbImg.addEventListener("pointerup", function (e) {
        if (lbStartX === null) {
          return;
        }
        var dx = e.clientX - lbStartX;
        var dy = e.clientY - lbStartY;
        lbStartX = null;
        if (Math.abs(dx) >= 40 && Math.abs(dx) >= Math.abs(dy) * 1.5) {
          showLightbox(lbIndex + (dx < 0 ? 1 : -1), "fullscreen_swipe");
        }
      });
      lbImg.addEventListener("dragstart", function (e) {
        e.preventDefault();
      });
      // Leave the page gallery on whatever photo was last viewed full-screen.
      lightbox.addEventListener("close", function () {
        document.documentElement.classList.remove("pr-lightbox-open");
        if (lbIndex !== activeIndex()) {
          thumbList[lbIndex].click();
        }
      });
    }

    var variantButtons = panel.querySelectorAll("[data-pr-showcase-variant]");
    var priceEl = panel.querySelector("[data-pr-showcase-price]");
    var totalEl = panel.querySelector("[data-pr-showcase-total]");
    var totalRow = panel.querySelector("[data-pr-showcase-total-row]");
    var addToCartBtn = panel.querySelector("[data-pr-showcase-add-to-cart]");
    var noteEl = panel.querySelector("[data-pr-showcase-note]");

    function recomputeTotal() {
      if (!addToCartBtn || !totalEl) {
        return;
      }
      var base = parseFloat(addToCartBtn.getAttribute("data-base-price") || "0");
      var addonsTotal = 0;
      var checked = panel.querySelectorAll("[data-pr-showcase-addon]:checked");
      checked.forEach(function (checkbox) {
        addonsTotal += parseFloat(checkbox.getAttribute("data-price") || "0");
      });
      totalEl.textContent = formatMoney(base + addonsTotal);
      // A size is always selected, so there is always a total — shown at all
      // times except when that size is sold out (no Add to cart to total for).
      if (totalRow) {
        totalRow.hidden = addToCartBtn.hidden;
      }
    }

    variantButtons.forEach(function (btn) {
      btn.addEventListener("click", function () {
        variantButtons.forEach(function (b) {
          b.classList.remove("pr-product-showcase__variant-pill--active");
        });
        btn.classList.add("pr-product-showcase__variant-pill--active");

        var variantId = btn.getAttribute("data-variant-id");
        var price = btn.getAttribute("data-price");
        var priceFormatted = btn.getAttribute("data-price-formatted");
        prTrack("size_select_" + prSlug(btn.getAttribute("data-variant-title")));

        if (addToCartBtn) {
          addToCartBtn.setAttribute("data-variant-id", variantId);
          addToCartBtn.setAttribute("data-base-price", price);
        }
        if (priceEl && priceFormatted) {
          priceEl.textContent = priceFormatted;
        }
        applyAvailability(btn);
        recomputeTotal();
      });
    });

    // Sold-out sizes stay selectable so the shopper can ask to be notified:
    // Add to Cart is swapped for the "Sold out — Notify me" block, which is
    // retargeted (email tag, WhatsApp message, label) to the chosen size.
    var notifyEl = panel.querySelector("[data-pr-notify]");
    var notifyToggle = panel.querySelector("[data-pr-notify-toggle]");
    var notifyOptions = panel.querySelector("[data-pr-notify-options]");

    function setNotifyOpen(open) {
      if (!notifyOptions) {
        return;
      }
      notifyOptions.hidden = !open;
      notifyToggle.setAttribute("aria-expanded", open ? "true" : "false");
    }

    function applyAvailability(pill) {
      if (!pill || !addToCartBtn || !notifyEl) {
        return;
      }
      var available = pill.getAttribute("data-available") === "true";
      var size = pill.getAttribute("data-variant-title");
      addToCartBtn.hidden = !available;
      notifyEl.hidden = available;
      setNotifyOpen(false);

      var sizeEl = notifyEl.querySelector("[data-pr-notify-size]");
      if (sizeEl) {
        sizeEl.textContent = size;
      }
      var tagBase = notifyEl.getAttribute("data-tag-base");
      var sizeSlug = size.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      var tagsInput = notifyEl.querySelector("[data-pr-notify-tags]");
      if (tagsInput) {
        tagsInput.value = "notify-me, " + tagBase + "-" + sizeSlug;
      }
      var waLink = notifyEl.querySelector("[data-pr-notify-whatsapp]");
      if (waLink) {
        var msg =
          "Hi Peak Reign! Please notify me when the " +
          notifyEl.getAttribute("data-product-title") +
          " (size " + size + ") is back in stock.";
        waLink.href = "https://wa.me/" + waLink.getAttribute("data-wa") + "?text=" + encodeURIComponent(msg);
      }
    }

    if (notifyToggle) {
      notifyToggle.addEventListener("click", function () {
        if (notifyOptions.hidden) {
          prTrack("notify_open");
        }
        setNotifyOpen(notifyOptions.hidden);
      });
    }

    applyAvailability(panel.querySelector(".pr-product-showcase__variant-pill--active"));

    // Shopify's customer form reloads the page with ?customer_posted=true.
    var notifyForm = panel.querySelector(".pr-notify__form");
    if (notifyForm) {
      notifyForm.addEventListener("submit", function () {
        prTrack("notify_email_submit");
      });
    }

    var details = panel.querySelector(".pr-product-showcase__details");
    if (details) {
      details.addEventListener("toggle", function () {
        if (details.open) {
          prTrack("product_details_open");
        }
      });
    }

    if (/[?&]customer_posted=true/.test(location.search) && noteEl) {
      prTrack("notify_signup_done");
      noteEl.textContent = "You're on the list — we'll email you as soon as it's back.";
      root.scrollIntoView({ block: "start" });
      history.replaceState(history.state, "", location.pathname + location.hash);
    }

    panel.querySelectorAll("[data-pr-showcase-addon]").forEach(function (checkbox) {
      checkbox.addEventListener("change", function () {
        prTrack((checkbox.checked ? "addon_add_" : "addon_remove_") + prSlug(checkbox.getAttribute("data-title")));
        recomputeTotal();
      });
    });

    recomputeTotal();

    // Sticky Add to Cart: while the visitor is anywhere in this product panel
    // (looking at photos, picking size/add-ons) but the real button is off
    // screen, a bottom bar mirrors it. Leaves as soon as the panel scrolls
    // away — unlike the earlier always-fixed bar that covered the whole page.
    if (addToCartBtn && "IntersectionObserver" in window) {
      var stickyBar = document.createElement("div");
      stickyBar.className = "pr-product-showcase__sticky-bar";
      stickyBar.setAttribute("aria-hidden", "true");
      var stickyBtn = document.createElement("button");
      stickyBtn.type = "button";
      stickyBtn.tabIndex = -1;
      stickyBtn.className = "pr-btn pr-btn--primary";
      // Running total rides along with the button — once the tiles and the
      // in-panel Total line are scrolled away it is the only price on screen.
      var stickyInner = document.createElement("div");
      stickyInner.className = "pr-product-showcase__sticky-inner";
      // Same "Total ₹X" row that sits above the in-panel button, stacked on
      // top of the sticky button so the two move as one unit.
      var stickyTotal = document.createElement("p");
      stickyTotal.className = "pr-product-showcase__total pr-product-showcase__sticky-total";
      var stickyTotalLabel = document.createElement("span");
      stickyTotalLabel.textContent = "Total";
      var stickyTotalAmount = document.createElement("span");
      stickyTotalAmount.className = "pr-product-showcase__sticky-amount";
      stickyTotal.appendChild(stickyTotalLabel);
      stickyTotal.appendChild(stickyTotalAmount);
      stickyInner.appendChild(stickyTotal);
      stickyInner.appendChild(stickyBtn);
      stickyBar.appendChild(stickyInner);
      document.body.appendChild(stickyBar);

      // Mirrors whichever primary action is showing: Add to Cart, or the
      // "Sold out — Notify me" toggle when the selected size is sold out.
      var soldOut = function () {
        return addToCartBtn.hidden && notifyToggle;
      };
      var syncStickyLabel = function () {
        var source = soldOut() ? notifyToggle : addToCartBtn;
        stickyBtn.textContent = source.textContent.replace(/\s+/g, " ").trim();
        stickyBtn.disabled = addToCartBtn.disabled && !soldOut();
        stickyTotalAmount.textContent = totalEl ? totalEl.textContent : "";
        stickyTotal.hidden = !totalEl || soldOut();
      };
      syncStickyLabel();
      if (totalEl) {
        new MutationObserver(syncStickyLabel).observe(totalEl, { subtree: true, childList: true, characterData: true });
      }
      new MutationObserver(syncStickyLabel).observe(addToCartBtn, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ["disabled", "hidden"]
      });
      stickyBtn.addEventListener("click", function () {
        prTrack(soldOut() ? "sticky_notify_click" : "sticky_cart_click");
        if (soldOut()) {
          setNotifyOpen(true);
          notifyEl.scrollIntoView({ behavior: "smooth", block: "center" });
          return;
        }
        addToCartBtn.click();
      });

      var panelVisible = false;
      var buttonVisible = false;
      var updateSticky = function () {
        stickyBar.classList.toggle(
          "pr-product-showcase__sticky-bar--visible",
          panelVisible && !buttonVisible && !panel.classList.contains("pr-product-showcase__panel--hidden")
        );
      };
      new IntersectionObserver(function (entries) {
        panelVisible = entries[entries.length - 1].isIntersecting;
        updateSticky();
      }).observe(panel);
      new IntersectionObserver(function (entries) {
        buttonVisible = entries[entries.length - 1].isIntersecting;
        updateSticky();
      }).observe(addToCartBtn.parentElement);
    }

    if (addToCartBtn) {
      addToCartBtn.addEventListener("click", function () {
        // Add-ons are packed inside the bag, not shipped separately, so the bag
        // line itself must say what goes in it. A visible "Add-ons" property
        // shows on the bag line in cart, checkout, the admin order and order
        // emails — "None" for a plain bag. Different values also keep each
        // configuration on its own bag line (otherwise a plain bag merges into
        // a customised one: 2 bags, 1 rain cover, no way to tell which).
        // Add-on variants still go in as their own nested lines (parent_id =
        // bag) so their inventory decrements and their price is charged.
        var bagVariantId = parseInt(addToCartBtn.getAttribute("data-variant-id"), 10);
        var addons = [];
        panel.querySelectorAll("[data-pr-showcase-addon]:checked").forEach(function (checkbox) {
          addons.push({
            id: parseInt(checkbox.getAttribute("data-variant-id"), 10),
            title: checkbox.getAttribute("data-title")
          });
        });
        addons.sort(function (a, b) {
          return a.title.localeCompare(b.title);
        });
        var addonLabel = addons.length
          ? addons.map(function (a) {
              return a.title;
            }).join(", ")
          : "None";
        var items = [
          { id: bagVariantId, quantity: 1, properties: { "Add-ons": addonLabel } }
        ];
        var selectedPill = panel.querySelector(".pr-product-showcase__variant-pill--active");
        prTrack("add_to_cart_click");
        if (selectedPill) {
          prTrack("add_to_cart_" + prSlug(selectedPill.getAttribute("data-variant-title")));
        }
        prTrack(addons.length ? "add_to_cart_with_addons" : "add_to_cart_no_addons");
        addons.forEach(function (a) {
          items.push({ id: a.id, quantity: 1, parent_id: bagVariantId });
        });

        addToCartBtn.disabled = true;
        if (noteEl) {
          noteEl.textContent = "Adding...";
        }

        // The theme's cart drawer is server-rendered at page load and only
        // re-renders when it hears a CartLinesUpdateEvent — a bare /cart/add.js
        // call left it showing the stale "cart is empty" markup even though the
        // cart really had items (badge said 1, drawer said empty).
        var deferred = CartLinesUpdateEvent.createPromise();
        document.dispatchEvent(
          new CartLinesUpdateEvent({
            action: "add",
            context: "product",
            lines: items.map(function (item) {
              return { merchandiseId: item.id, quantity: item.quantity };
            }),
            promise: deferred.promise
          })
        );

        fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: items })
        })
          .then(function (res) {
            return res.json().then(function (body) {
              if (!res.ok || body.status) {
                throw new Error(body.description || body.message || "Add to cart failed");
              }
              return body;
            });
          })
          .then(function () {
            prTrack("add_to_cart_success");
            if (noteEl) {
              noteEl.textContent = "Added to cart.";
            }
            return fetch("/cart.js");
          })
          .then(function (res) {
            return res.json();
          })
          .then(function (cart) {
            deferred.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
              detail: { didError: false, items: cart.items, itemCount: cart.item_count }
            });
          })
          .catch(function (err) {
            deferred.reject(err);
            console.error("pr-product-showcase: add to cart failed", err);
            prTrack("add_to_cart_fail");
            if (noteEl) {
              noteEl.textContent = err.message || "Something went wrong — please try again.";
            }
          })
          .finally(function () {
            addToCartBtn.disabled = false;
          });
      });
    }
  });
})();
